import {
  Box,
  CircularProgress,
  ClickAwayListener,
  InputBase,
  Typography,
  useTheme
} from '@material-ui/core';
import { useEffect, useMemo, useRef } from 'react';
import { useDropzone } from 'react-dropzone';

import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import ArrowUpwardRoundedIcon from '@material-ui/icons/ArrowUpwardRounded';

import { SButton } from '@setvi/shared/components';
import { RefreshIcon } from '@setvi/shared/components/sicons';
import { TextColor } from '@setvi/shared/enums';
import {
  Image,
  Container,
  RemoveImage,
  InputContent,
  DropzoneContainer
} from './styled';
import { SearchFormProps } from '..';
import { useImageInput } from './hook';
import DNDOverlay from './overlay';
import { formId } from '../text';
import { ProductMatchValues } from '../../..';
import { SearchFormOptions } from '../../../enums';

export const acceptedFileTypes: Record<string, string[]> = {
  'image/*': ['.jpeg', '.jpg', '.png']
};

interface ImageInputProps
  extends Omit<SearchFormProps, 'disableButton' | 'urlRows' | 'textRows'> {
  rows?: number;
  handleInputValidation: (invalid: boolean, tooltip?: string) => void;
}

const MAX_DESCRIPTION_LENGTH = 500;

const ImageInput = ({
  values,
  rows = 6,
  onSubmit,
  onChange,
  handleInputValidation
}: ImageInputProps) => {
  const inputRef = useRef(null);

  const hasImage = !!values?.[ProductMatchValues.Image];
  const hasDescription = !!values?.[ProductMatchValues.Description];

  const { palette } = useTheme();
  const {
    error,
    focus,
    loading,
    onDrop,
    setFocus,
    onRemoveImage,
    onDropRejected
  } = useImageInput({
    onChange
  });

  const { open, getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted: onDrop,
    onDropRejected,
    noKeyboard: true,
    noClick: false,
    maxFiles: 1,
    disabled: loading,
    accept: acceptedFileTypes
  });

  useEffect(() => {
    const invalidaImage = !values?.[ProductMatchValues.Image];
    const invalidDescription = !values?.[ProductMatchValues.Description];
    const invalidSizeDescription =
      values?.[ProductMatchValues.Description]?.length > MAX_DESCRIPTION_LENGTH;

    const invalid =
      invalidaImage || invalidDescription || invalidSizeDescription;
    const tooltipImage = invalidaImage
      ? `Image upload is mandatory for "Image Match" AI Search.`
      : '';
    const tooltipDescReq = invalidDescription
      ? `Description is mandatory for "Image Match" AI Search.`
      : '';
    const tooltipDescription = invalidSizeDescription
      ? `Description must be less than ${MAX_DESCRIPTION_LENGTH} characters.`
      : '';

    handleInputValidation?.(
      !invalid,
      tooltipImage || tooltipDescription || tooltipDescReq
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values?.[ProductMatchValues.Image],
    values?.[ProductMatchValues.Description]
  ]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!hasImage || error || !hasDescription) {
      return;
    }

    onSubmit?.(SearchFormOptions.image, {
      value: values?.[ProductMatchValues.Image],
      [ProductMatchValues.Image]: values?.[ProductMatchValues.Image],
      [ProductMatchValues.Description]: values?.description
    });
  };

  const imageStyle = useMemo(() => {
    if (hasImage && error) {
      return {
        borderColor: '#FA2020',
        bgcolor: '#FED2D2'
      };
    }

    return {
      borderColor: !hasImage || loading ? '#1E96FC' : '#23A566',
      bgcolor: !hasImage || loading ? '#EBF5FF' : '#E6FAF0'
    };
  }, [hasImage, loading, error]);

  return (
    <ClickAwayListener onClickAway={() => setFocus(false)}>
      <form id={formId} onSubmit={handleSubmit}>
        <Container>
          <InputContent>
            <DropzoneContainer {...getRootProps()} sx={imageStyle}>
              <input {...getInputProps()} />

              <DNDOverlay visible={isDragActive} />

              {loading ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gridGap="12px">
                  <CircularProgress size={35} />
                  <Typography
                    style={{
                      fontSize: 14
                    }}>
                    Uploading image...
                  </Typography>
                </Box>
              ) : (
                <>
                  {!hasImage ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gridGap={12}
                      p={3}>
                      <SButton
                        startIcon={
                          <ArrowUpwardRoundedIcon
                            style={{
                              height: 18
                            }}
                          />
                        }
                        style={{
                          borderRadius: 120,
                          minHeight: 32
                        }}>
                        Upload Image
                      </SButton>

                      {error && (
                        <Typography
                          variant="body2"
                          style={{ color: '#f44336' }}>
                          Please upload image
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gridGap={12}>
                      <Box position="relative" width="fit-content">
                        <Image
                          alt="Uploaded image"
                          src={values?.[ProductMatchValues.Image]}
                        />
                        <RemoveImage
                          onClick={e => {
                            e.stopPropagation();
                            onRemoveImage();
                          }}>
                          <CloseRoundedIcon htmlColor="#fff" />
                        </RemoveImage>
                      </Box>

                      <SButton
                        variant="outlined"
                        color="primary"
                        style={{
                          borderRadius: 120,
                          fontSize: 14,
                          minHeight: 32
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          open();
                        }}
                        disabled={loading}
                        startIcon={<RefreshIcon fill={TextColor.Blue} />}>
                        Reupload image
                      </SButton>
                    </Box>
                  )}
                </>
              )}
            </DropzoneContainer>

            <Box
              position="relative"
              // @ts-ignore
              ref={inputRef}
              onClick={() => {
                setFocus(true);
                const textarea = inputRef.current?.querySelector('textarea');

                if (textarea) {
                  textarea.focus();
                }
              }}>
              <InputBase
                fullWidth
                multiline
                rows={rows}
                required
                placeholder="Please describe this image and use case for the best of your ability to find an accurate image match."
                value={values?.[ProductMatchValues.Description]}
                onChange={e => {
                  if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
                    onChange({
                      [ProductMatchValues.Description]: e.target.value
                    });
                  }
                }}
                inputProps={{
                  maxLength: MAX_DESCRIPTION_LENGTH
                }}
                style={{
                  padding: '12px',
                  border: '1px solid #E2E2EA',
                  borderRadius: '8px',
                  fontSize: 14,
                  borderColor: focus ? palette.primary.main : palette.grey[200]
                }}
              />
            </Box>
          </InputContent>
        </Container>
      </form>
    </ClickAwayListener>
  );
};

export default ImageInput;
